import { useCallback } from 'react';
import { useLock } from '../../hooks/useLock';
import { useMoveQuantFile } from '../../hooks/useMoveQuantFile';
import { ButtonSet } from '../Modal/ButtonSet.js';
import { Header } from '../Modal/Header.js';
import { Modal } from '../Modal/Modal.js';
import { ActivityIndicator } from '../presentation/ActivityIndicator.js';
import { Button } from '../presentation/Button/index.js';
import { ButtonOutlined } from '../presentation/ButtonOutlined.js';
import { FolderSelect } from './FolderSelect';
import { QuantFolderTreeView } from './QuantFolderTreeView';
import styles from './style/ModalMoveQuantFile.module.css';

type Props = {
  onClose: () => unknown;
  open: boolean;
  quantAnalysisId: number;
  projectId: number;
  itemId: number;
  itemName?: string;
  quantAnalysisName?: string;
};

export const ModalMoveQuantFile = ({ onClose, open, quantAnalysisId, projectId, itemId, itemName, quantAnalysisName }: Props) => {
  const [, lock] = useLock();

  const { currentPath, data, availableDestinations, loadingTree, handleMove, onSelectFolder, selectedFolderId } = useMoveQuantFile({
    quantAnalysisId,
    projectId,
    itemId,
    quantAnalysisName,
  });

  const handleSave = useCallback(() => {
    return handleMove().then(onClose);
  }, [handleMove, onClose]);

  // Since we filter out the current location, user can always move to any available destination
  const canSave = data !== null && !loadingTree;

  const title = `Move ${itemName || 'File'}`;

  return (
    <Modal
      className={styles.root}
      onClose={onClose}
      open={open}>
      <Header
        className={styles.header}
        text={title} />
      <div className={styles.item}>
        <div className={styles.label}>Current Location:</div>
        <div className={styles.path}>{currentPath.join('')}</div>
      </div>
      <div className={styles.item}>
        <div className={styles.label}>Destination:</div>
        <FolderSelect
          className={styles.select}
          options={availableDestinations}
          value={selectedFolderId}
          onChange={onSelectFolder} />
      </div>
      <div className={styles.body}>
        {loadingTree && <ActivityIndicator />}
        {!loadingTree && data && (
          <QuantFolderTreeView
            data={data}
            selectedFolderId={selectedFolderId}
            onNodeSelection={id => onSelectFolder(String(id))} />
        )}
      </div>
      <ButtonSet>
        <ButtonOutlined
          className={styles.btn}
          onClick={onClose}>
          Cancel
        </ButtonOutlined>
        <Button
          className={styles.btn}
          disabled={!canSave}
          onClick={lock(handleSave)}>
          Move
        </Button>
      </ButtonSet>
    </Modal>
  );
};

export default ModalMoveQuantFile;
